import { emptyCityHero } from '../../../shared/empty-objects';
import { omitDeepTypename } from '../../../utils/pass-graphql';
import { type HeroSlide } from './HeroSlides';

export const HeroSlidesActions = {
  SET_SLIDES_FROM_PROPS: 'SET_SLIDES_FROM_PROPS',
  UPDATE_SLIDES: 'UPDATE_SLIDES',
  CREATE_NEW_SLIDE: 'CREATE_NEW_SLIDE',
  DELETE_SLIDE: 'DELETE_SLIDE',
  REARRANGE_SLIDES: 'REARRANGE_SLIDES',
} as const;

type State = {
  slides: HeroSlide[];
  slidesToDelete: HeroSlide['_id'][];
};

type Action =
  | {
      type: typeof HeroSlidesActions.SET_SLIDES_FROM_PROPS;
      update: State;
    }
  | {
      type: typeof HeroSlidesActions.UPDATE_SLIDES;
      update: Partial<HeroSlide>;
      index: number;
    }
  | {
      type: typeof HeroSlidesActions.CREATE_NEW_SLIDE;
    }
  | {
      type: typeof HeroSlidesActions.DELETE_SLIDE;
      index: number;
    }
  | {
      type: typeof HeroSlidesActions.REARRANGE_SLIDES;
      direction: number;
      index: number;
    };

export const initialState: State = {
  slides: [],
  slidesToDelete: [],
};

// Closure function that generates a unique ID for each new slide added
const newSlideGenerator = () => {
  let currSlideId = 0;

  return () => {
    currSlideId += 1;

    return {
      _id: `NEW_${currSlideId.toString()}`, // Note: This pattern is relied on in certain files to determine whether to create or update a slide. See NationalSlides.tsx.
      ...emptyCityHero,
    } satisfies HeroSlide;
  };
};

export default (state: State, action: Action): State => {
  const createNewSlide = newSlideGenerator();
  switch (action.type) {
    case HeroSlidesActions.SET_SLIDES_FROM_PROPS:
      return { ...omitDeepTypename(action.update) };
    case HeroSlidesActions.UPDATE_SLIDES: {
      const { slides } = state;
      const { index, update } = action;
      slides[index] = { ...slides[index]!, ...update };
      return { ...state, slides };
    }
    case HeroSlidesActions.CREATE_NEW_SLIDE: {
      const { slides } = state;
      const newSlides = [...slides, createNewSlide()];
      return { ...state, slides: newSlides };
    }
    case HeroSlidesActions.DELETE_SLIDE: {
      const { slides, slidesToDelete } = state;
      const { index } = action;
      const deletedSlide = slides.splice(index, 1)[0];

      if (deletedSlide && !deletedSlide._id.startsWith('NEW_')) {
        slidesToDelete.push(deletedSlide._id);
      }

      return { slidesToDelete, slides };
    }
    case HeroSlidesActions.REARRANGE_SLIDES: {
      const { slides = [] } = state;
      const { direction, index } = action;
      const storedSlide = slides.splice(index, 1);
      slides.splice(index + direction, 0, storedSlide[0]!);
      return { ...state, slides };
    }
    default:
      // @ts-ignore
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};
